import { useState, useEffect, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, RefreshCw, QrCode } from 'lucide-react';
import QRCodeLib from 'qrcode';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FRONTEND_URL = window.location.origin;

export default function BusinessQRCode() {
  const [qrCodeData, setQrCodeData] = useState(null);
  const [qrImageUrl, setQrImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    fetchQRCode();
  }, []);

  useEffect(() => {
    if (qrCodeData) {
      generateQRImage();
    }
  }, [qrCodeData]);

  const fetchQRCode = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/business/qr-code`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQrCodeData(response.data);
    } catch (error) {
      console.error('Error fetching QR code:', error);
      toast.error('Failed to load QR code');
    } finally {
      setLoading(false);
    }
  };

  const generateQRImage = async () => {
    if (!qrCodeData || !canvasRef.current) return;

    try {
      // Generate QR code URL that points to join page
      const joinUrl = `${FRONTEND_URL}/join/business/${qrCodeData.qr_code_data}`;
      
      // Generate QR code on canvas
      await QRCodeLib.toCanvas(canvasRef.current, joinUrl, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });

      // Convert canvas to data URL for download
      const dataUrl = canvasRef.current.toDataURL('image/png');
      setQrImageUrl(dataUrl);
    } catch (error) {
      console.error('Error generating QR code:', error);
      toast.error('Failed to generate QR code image');
    }
  };

  const handleRegenerateQR = async () => {
    try {
      setRegenerating(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/business/regenerate-qr`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQrCodeData(response.data);
      toast.success('QR code regenerated successfully');
    } catch (error) {
      console.error('Error regenerating QR code:', error);
      toast.error('Failed to regenerate QR code');
    } finally {
      setRegenerating(false);
    }
  };

  const handleDownloadQR = () => {
    if (!qrImageUrl) return;

    const link = document.createElement('a');
    link.download = `${qrCodeData.business_name.replace(/\s+/g, '_')}_QRCode.png`;
    link.href = qrImageUrl;
    link.click();
    toast.success('QR code downloaded successfully');
  };

  if (loading) {
    return (
      <Card className="p-6 text-center">
        <QrCode className="w-12 h-12 animate-pulse text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">Loading QR code...</p>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <h3 className="font-playfair font-bold text-xl sm:text-2xl text-primary mb-4 text-center">
        Business QR Code
      </h3>
      <p className="text-sm sm:text-base text-gray-600 mb-4 text-center">
        Customers can scan this QR code to join your business
      </p>

      {/* QR Code Display */}
      <div className="bg-white border-4 border-gray-200 rounded-lg p-4 mb-4 flex justify-center">
        <canvas ref={canvasRef} className="max-w-full" />
      </div>

      {/* QR Code ID */}
      <div className="bg-gray-50 rounded-lg p-3 mb-4 text-center">
        <p className="text-xs text-gray-500 mb-1">QR Code ID</p>
        <p className="text-sm font-mono font-bold text-gray-900 break-all">{qrCodeData?.qr_code_data}</p>
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Button
          onClick={handleDownloadQR}
          className="bg-indigo-600 hover:bg-indigo-700 w-full"
          disabled={!qrImageUrl}
        >
          <Download className="w-4 h-4 mr-2" />
          Download QR Code
        </Button>
        <Button
          onClick={handleRegenerateQR}
          variant="outline"
          className="w-full"
          disabled={regenerating}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${regenerating ? 'animate-spin' : ''}`} />
          Regenerate QR Code
        </Button>
      </div>

      {/* Instructions */}
      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-xs sm:text-sm text-blue-900">
          <strong>💡 Tip:</strong> Download and print this QR code to display at your business location. 
          Customers can scan it to join your business and participate in your events!
        </p>
      </div>
    </Card>
  );
}
